import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { ShoppingBag, Bell, LogOut, ShieldCheck, Clock, Flame, Trophy, Zap } from "lucide-react";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [rem, setRem] = useState(user?.reminders || { am: "07:30", pm: "19:30", enabled: true });
  const [saving, setSaving] = useState(false);

  const saveReminders = async (next) => {
    setRem(next); setSaving(true);
    try { await api.post("/profile/reminders", next); await refresh(); toast.success("Reminders updated"); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const doLogout = async () => { await logout(); navigate("/welcome", { replace: true }); };

  const p = user?.profile || {};
  const stats = [
    { icon: Flame, label: "Streak", value: `${user?.journal_streak || 0}d` },
    { icon: Trophy, label: "Completed", value: user?.stories_completed || 0 },
    { icon: Zap, label: "Minutes", value: Math.round(user?.total_minutes || 0) },
  ];

  return (
    <div className="relative flex-1 overflow-y-auto mm-scroll bg-white mm-glow" data-testid="profile-page">
      <div className="relative z-10 px-5 pt-7 pb-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-royal to-turq flex items-center justify-center text-white font-serif text-2xl font-bold">
            {(user?.name || "A").charAt(0)}
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-bold text-navy truncate">{user?.name}</h1>
            <p className="text-slate-500 text-sm truncate">{user?.email}</p>
            <p className="text-xs text-royal font-semibold capitalize mt-0.5">
              {p.sport} · {p.position} {p.throws && `· T:${p.throws}`} {p.bats && `B:${p.bats}`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5 mt-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-center" data-testid={`stat-${s.label.toLowerCase()}`}>
              <s.icon size={18} className="mx-auto text-royal" />
              <div className="font-bold text-navy text-lg mt-1">{s.value}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-navy text-white p-5 flex items-center justify-between" data-testid="profile-credits">
          <div>
            <div className="text-white/60 text-xs uppercase tracking-wider font-semibold">Sessions left</div>
            <div className="font-serif text-3xl font-bold">{user?.credits || 0}</div>
          </div>
          <button data-testid="profile-buy-btn" onClick={() => navigate("/paywall")}
            className="flex items-center gap-2 bg-white text-navy font-bold px-4 py-2.5 rounded-full active:scale-95 transition">
            <ShoppingBag size={16} /> Get more
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-bold text-navy"><Bell size={18} /> Reminders</div>
            <button data-testid="reminders-toggle" onClick={() => saveReminders({ ...rem, enabled: !rem.enabled })}
              className={`w-11 h-6 rounded-full transition relative ${rem.enabled ? "bg-royal" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${rem.enabled ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <label className="text-sm">
              <span className="flex items-center gap-1.5 text-slate-500 font-medium mb-1"><Clock size={13} /> Morning</span>
              <input data-testid="reminder-am" type="time" value={rem.am} disabled={!rem.enabled || saving}
                onChange={(e) => saveReminders({ ...rem, am: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-100 outline-none disabled:opacity-50" />
            </label>
            <label className="text-sm">
              <span className="flex items-center gap-1.5 text-slate-500 font-medium mb-1"><Clock size={13} /> Evening</span>
              <input data-testid="reminder-pm" type="time" value={rem.pm} disabled={!rem.enabled || saving}
                onChange={(e) => saveReminders({ ...rem, pm: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-100 outline-none disabled:opacity-50" />
            </label>
          </div>
        </div>

        {user?.role === "admin" && (
          <button data-testid="profile-admin-btn" onClick={() => navigate("/admin")}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold py-3.5 rounded-2xl active:scale-[0.98] transition">
            <ShieldCheck size={18} /> Admin dashboard
          </button>
        )}

        <button data-testid="profile-logout-btn" onClick={doLogout}
          className="mt-4 w-full flex items-center justify-center gap-2 text-rose-500 font-semibold py-3.5 rounded-2xl border border-rose-200 active:scale-[0.98] transition">
          <LogOut size={18} /> Log out
        </button>

        <div className="mt-8 flex flex-col items-center gap-2 opacity-60">
          <Logo size={28} />
          <p className="text-[11px] text-slate-400 text-center px-6">
            MindMoves is a supportive wellness tool, not medical advice. 13+ only.
          </p>
        </div>
      </div>
    </div>
  );
}
